import React, { useContext } from 'react'
import { useSurveyContext } from '../../Context/SurveyCustomHooks/useSurveyContext';
import { languageContext } from '../../../../../context/languageContext';

export const RadioQuestion = () => {
    const language = useContext(languageContext);
    const {
        activeStep,
        surveyQuestions,
        setSurveyQuestions,
        answers,
        updateAnswers,
        getLanguageOptions,
        handleNext,
        getDependentAnswers,
        deleteDependentAnswers,
        // isSmallScreen,
        surveyInfo
    } = useSurveyContext();
    const question = surveyQuestions[activeStep];
    const acceptedEmojiAnswers = ["1", "2", "3", "4", "5"];
    const emojiPack = question?.emoji_pack;
    const options = getLanguageOptions(question, language.locale);
    // const hasDendency = (surveyQuestions[activeStep].question_type === 4 || surveyQuestions[activeStep].depentId.length > 0)

    const selectedValue = answers?.find(answer => answer.survey_item_id === question.itemId)?.answer_value;
    const hasDependencyQuestions = question?.depentId.length > 0;

    /**
     * Handles the radio button click event.
     * If the clicked radio is already selected, it deletes dependent answers and questions.
     * 
     * @param {React.MouseEvent} e - The click event object containing the value of the clicked radio button.
     * 
     * @remarks
     * This function:
     * 1. Gets the value of the clicked radio button.
     * 2. Checks if it's the same as the currently selected value.
     * 3. If it is the same, it deletes the next dependent question and any dependent answers.
     * 
     * Note: The code for removing the answer itself is commented out.
     */
    const handleRadioClick = (e) => {
        const clickedValue = e.target.value;
        if (selectedValue === clickedValue) {
            //delete the answer if the same option is clicked again
            // answers?.findIndex(answer => answer.survey_item_id === question.itemId)
            // const newAnswers = answers.filter(answer => answer.survey_item_id !== question.itemId);
            // setAnswers(newAnswers);
            deleteNextDependentQuestion(e.target.value);
            const itemsToDelete = getDependentAnswers(question);
            deleteDependentAnswers(itemsToDelete);
        }
    };

    /**
     * Deletes dependent questions based on the current answer value.
     * 
     * This function handles three main cases:
     * 1. If the current question is the last one and has no dependencies, nothing happens.
     * 2. If there is a next non-dependent question, it removes all questions between the current 
     *    and the next non-dependent question.
     * 3. For the last question with dependencies, it removes all subsequent dependent questions 
     *    if the current answer doesn't match any dependency.
     * 
     * For all cases where dependent questions are removed, it also cleans up any dependent answers.
     * 
     * @param {string|number} currentValue - The current selected answer value that determines which dependent questions to show or hide
     * @returns {void}
     */
    const deleteNextDependentQuestion = (currentValue) => {
        //check if the question has next question
        if (activeStep + 1 >= surveyQuestions.length && !surveyQuestions[activeStep].depentId.length > 0) {
            return;
        }
        //* Get the next not dependent question
        const nextNonDependentQuestionIndex = surveyQuestions.findIndex((q, index) =>
            index > activeStep && q && (q.question_type !== 4)
        );
        if (nextNonDependentQuestionIndex !== -1) {
            //* Remove the following questions until the next non-dependent question
            setSurveyQuestions(prevState => {
                const newState = [...prevState];
                if (nextNonDependentQuestionIndex - activeStep - 1 > 0) {
                    newState.splice(activeStep + 1, nextNonDependentQuestionIndex - activeStep - 1);
                }
                return newState;
            });
            //Toco asi porque una de las respuestas no abria mas preguntas y no eliminaba las respuestas de las reguntas dependientes
            surveyQuestions[activeStep].depentId.forEach((item) => {
                const itemsToDelete = getDependentAnswers(item.dependentQuestion);
                deleteDependentAnswers(itemsToDelete);
            })
        } else if (surveyQuestions[activeStep].depentId.length > 0) {
            //* Esto es condiciones para la ULTIMA PREGUNTA porque no tiene siguiente pregunta
            const openValue = surveyQuestions[activeStep].depentId.find(item => item.optionvalue === currentValue);
            //* Mira si la pregunta tiene dependencias y si la respuesta es la misma
            //* Si si es es porque va haber mas preguntas dependientes que le seguiran
            if (openValue) {
                //delete all the next questions until the last surveyQuestion.length
                setSurveyQuestions(prevState => {
                    const newState = [...prevState];
                    newState.splice(activeStep + 1, surveyQuestions.length - activeStep - 1);
                    return newState;
                });
            } else if (activeStep + 1 < surveyQuestions.length) {
                //*Aqui simplemete eliminas todas las preguntas de la siguiente pregunta si esta no abre nuevas preguntas
                setSurveyQuestions(prevState => {
                    const newState = [...prevState];
                    newState.splice(activeStep + 1, surveyQuestions.length - activeStep - 1);
                    return newState;
                });
            }
            //Toco asi porque una de las respuestas no abria mas preguntas y no eliminaba las respuestas de las reguntas dependientes
            surveyQuestions[activeStep].depentId.forEach((item) => {
                const itemsToDelete = getDependentAnswers(item.dependentQuestion);
                deleteDependentAnswers(itemsToDelete);
            })
        }
    }

    /**
     * Handles radio button selection change.
     * @param {React.ChangeEvent<HTMLInputElement>} e - The event object from the radio input change.
     * @description 
     * This function performs several actions when a radio option is selected:
     * 1. If the question has dependency questions:
     *    - Deletes any next dependent questions based on the selected value
     *    - Finds the dependent question associated with the selected option
     *    - Inserts the dependent question after the current question in the survey
     *    - Deletes answers related to the dependent questions
     * 2. Updates the answer for the current question
     * 3. Finds the next non-dependent question (excluding type 4)
     * 4. Automatically advances to the next question if a non-dependent question exists
     */
    const handleRadioChange = (e) => {
        //append new question next to the current question
        if (hasDependencyQuestions) {
            deleteNextDependentQuestion(e.target.value);
            // const nextQuestion = surveyQuestions[activeStep + 1];
            const questionIndex = question.depentId.findIndex(item => item.optionvalue === e.target.value);
            if (questionIndex !== -1) {
                setSurveyQuestions(prevState => {
                    const newState = [...prevState];
                    newState.splice(activeStep + 1, 0, question.depentId[questionIndex].dependentQuestion);
                    return newState;
                });
                const itemsToDelete = getDependentAnswers(question.depentId[questionIndex].dependentQuestion);
                deleteDependentAnswers(itemsToDelete);
            }
        }
        //TODO: Hacer la opcion de otros
        updateAnswers(question.itemId, e.target.value);
        const nextNonDependentQuestionIndex = surveyQuestions.findIndex((q, index) =>
            index > activeStep && q && (q.question_type !== 4)
        );
        //* Esta es para que no siga automaticamente si la pregunta tiene dependientes
        //* porque falla cuando deselecciono una respuesta y como deseleccione lo que hace es que
        //* intenta ir a la siguiente pregunta pero como ya no existe la siguiente pregunta genera un erro
        nextNonDependentQuestionIndex !== -1 && handleNext(true);
    }
    const beginningAndEnd = (options) => {
        let values = [];
        options.forEach((e) => {
            !isNaN(e) && values.push(parseInt(e))
        });
        //[1,10], [1,5]
        return [Math.min(...values), Math.max(...values)]
    }

    return (
        options.length > 0 ?
            options.map((option, questionIndex) => {
                return (
                    <div className="form-check-inline text-center align-top" key={`radio${activeStep}${questionIndex}`}>
                        {surveyInfo?.numericHand && Number(option) === beginningAndEnd(options)[1] ?
                            <div className="text-muted small d-flex justify-content-center">
                                <i className="fa fa-thumbs-up fa-2x" style={{ color: "gray" }} aria-hidden="true" />
                            </div>
                            :
                            <div className="text-muted small d-flex justify-content-center" style={{ visibility: "hidden" }}>
                                <i className="fa fa-thumbs-up fa-2x" style={{ color: "gray" }} aria-hidden="true" />
                            </div>
                        }
                        <input
                            className="btn-check RegularMadeTommy"
                            type="radio"
                            name={`radio${activeStep}`}
                            id={`radio${activeStep}${questionIndex}`}
                            //Solo Compara los valores de las opciones en español y las guarda
                            value={question.options[questionIndex]}
                            checked={selectedValue === question.options[questionIndex]}
                            onChange={handleRadioChange}
                            onClick={handleRadioClick}
                        />
                        {!(emojiPack === '') && acceptedEmojiAnswers.includes(option) ?
                            <label
                                className="btn btn-outline-dark mt-1 RegularMadeTommy"
                                htmlFor={`radio${activeStep}${questionIndex}`}
                            >
                                <br />
                                <img
                                    alt={option}
                                    className="mySurveyEmoji"
                                    src={`https://storage.googleapis.com/server-7bytes.appspot.com/Emoji%20Packs/Pack%20${emojiPack.match(/\d+/)[0]}/Pack%20${emojiPack.match(/\d+/)[0]}.${option}.png`}
                                />
                            </label>
                            :
                            <label
                                className="btn btn-outline-dark mt-1"
                                htmlFor={`radio${activeStep}${questionIndex}`}
                                style={{ 
                                    minWidth: "41px", 
                                    marginRight: "5px" 
                                }}
                            >
                                {/* Si algunas de las opciones es nula coloca la opcion en español */}
                                {option ? option : question.options[questionIndex]}
                            </label>
                        }
                        {surveyInfo?.numericHand && Number(option) <= beginningAndEnd(options)[0] && (
                            <div className="text-muted small d-flex justify-content-center mt-1">
                                <i className="fa fa-thumbs-down fa-2x" style={{ color: "gray" }} aria-hidden="true" />
                            </div>
                        )}
                    </div>
                )
            })
            :
            <h6 style={{ color: "red" }}>Error</h6>
    )
}
